import React from 'react'
import { useState, useCallback, useMemo } from 'react'

// Static category constants
export const TERMINALS_CATEGORY = 'general.terminals'
export const PERSONS_CATEGORY = 'general.people'
export const NOTIFICATIONS_CATEGORY = 'general.deviations'

type SearchResultItem = { url: string; key: number; text: string; index: number }
type SearchResult = { [key: string]: SearchResultItem[] }

interface HeaderSearchProviderValue {
  focus: boolean
  loading: boolean
  searchPhrase: string
  activeIndex: number
  activeLink: string
  nrOfLink: number
  searchResult: SearchResult
  actions: {
    focusSearch: () => void
    setSearchPhrase: (searchPhrase: string) => void
    decreaseActiveIndex: () => void
    increaseActiveIndex: () => void
    resetActiveIndex: () => void
    setData: (terminals: any[], persons: any[]) => void
    hasSearchResult: () => boolean
    clearSearchResult: () => void
    getActiveLink: () => string
    setLoading: (loading: boolean) => void
  }
}

const HeaderSearchContext = React.createContext<HeaderSearchProviderValue | null>(null)

const getDefaultSearchResult = (): SearchResult => ({
  [TERMINALS_CATEGORY]: [],
  [PERSONS_CATEGORY]: [],
  [NOTIFICATIONS_CATEGORY]: [],
})

const HeaderSearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [focus, setFocus] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchPhrase, setSearchPhrase] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [nrOfLink, setNrOfLink] = useState(0)
  const [searchResult, setSearchResult] = useState<SearchResult>(getDefaultSearchResult)

  const updateActiveIndex = useCallback(
    (index: number) => {
      let newIndex = index
      if (newIndex < 0) {
        newIndex = 0
      } else if (newIndex >= nrOfLink) {
        newIndex = nrOfLink - 1
      }
      setActiveIndex(newIndex)
    },
    [nrOfLink],
  )

  const setData = useCallback((terminals: any[], persons: any[]) => {
    const newSearchResult: SearchResult = {
      [TERMINALS_CATEGORY]: [],
      [PERSONS_CATEGORY]: [],
      [NOTIFICATIONS_CATEGORY]: [],
    }

    let currIndex = -1
    const terminalNotifications: any[] = []

    // Categories are placed in rows. To make index grow per column, we add notifications before persons
    terminals.forEach((t) => {
      terminalNotifications.push({
        url: '/newNotifications?filterText=' + encodeURIComponent(t.name),
        key: t.id,
        text: t.name + ' - ' + t.description + ' - ' + t.id,
      })
      newSearchResult[TERMINALS_CATEGORY].push({
        url: '/terminals/' + t.id,
        key: t.id,
        text: t.name + ' - ' + t.description + ' - ' + t.id,
        index: ++currIndex,
      })
    })

    terminalNotifications.forEach((termNoti) =>
      newSearchResult[NOTIFICATIONS_CATEGORY].push({
        ...termNoti,
        index: ++currIndex,
      }),
    )

    persons.forEach((p) =>
      newSearchResult[NOTIFICATIONS_CATEGORY].push({
        url: `/newNotifications?ipPersonId=${p.id}&filterText=${encodeURIComponent(p.firstName + ' ' + p.surName)}`,
        key: p.id,
        text: p.firstName + ' ' + p.surName + ' - ' + p.id,
        index: ++currIndex,
      }),
    )

    persons.forEach((p) =>
      newSearchResult[PERSONS_CATEGORY].push({
        url: `/persons?person=${p.id}&selectedPerson=${p.id}`,
        key: p.id,
        text: p.firstName + ' ' + p.surName + ' - ' + p.id,
        index: ++currIndex,
      }),
    )

    setSearchResult(newSearchResult)
    setNrOfLink(currIndex + 1)
  }, [])

  const getUrlFromSearchResult = useCallback(
    (index: number): string => {
      for (const category of Object.keys(searchResult)) {
        const found = searchResult[category].find((e) => e.index === index)
        if (found) {
          return found.url
        }
      }
      return ''
    },
    [searchResult],
  )

  const clearSearchResult = useCallback(() => {
    setSearchResult(getDefaultSearchResult())
    setNrOfLink(0)
    setFocus(false)
    setSearchPhrase('')
  }, [])

  const hasSearchResult = useCallback(() => {
    const terminalsResult = searchResult[TERMINALS_CATEGORY]
    const personsResult = searchResult[PERSONS_CATEGORY]
    const notiResult = searchResult[NOTIFICATIONS_CATEGORY]
    return terminalsResult.length > 0 || personsResult.length > 0 || notiResult.length > 0
  }, [searchResult])

  const getActiveLink = useCallback(() => {
    const url = getUrlFromSearchResult(activeIndex)
    clearSearchResult()
    return url
  }, [activeIndex, getUrlFromSearchResult, clearSearchResult])

  const value = useMemo<HeaderSearchProviderValue>(
    () => ({
      focus,
      loading,
      searchPhrase,
      activeIndex,
      activeLink: '',
      nrOfLink,
      searchResult,
      actions: {
        focusSearch: () => setFocus(true),
        setSearchPhrase,
        decreaseActiveIndex: () => updateActiveIndex(activeIndex - 1),
        increaseActiveIndex: () => updateActiveIndex(activeIndex + 1),
        resetActiveIndex: () => updateActiveIndex(0),
        setData,
        hasSearchResult,
        clearSearchResult,
        getActiveLink,
        setLoading,
      },
    }),
    [
      focus,
      loading,
      searchPhrase,
      activeIndex,
      nrOfLink,
      searchResult,
      updateActiveIndex,
      setData,
      hasSearchResult,
      clearSearchResult,
      getActiveLink,
    ],
  )

  return <HeaderSearchContext.Provider value={value}>{children}</HeaderSearchContext.Provider>
}

export { HeaderSearchProvider, HeaderSearchContext }
